import React, { useState } from 'react';
import Box from '@mui/material/Box';
import './OrganizationDetailsCard.css';
import { useTheme } from '@mui/material/styles';
import OrgImage from '../OrgComponets/OrgTile/OrgImage';
import EditOrganizationModal from './EditModal';
import OrganizationServices from 'services/OrganizationServices';
import BranchesServices from 'services/BranchesServices';
import Progress from 'components/Progress';
import Button from 'components/Button';
import CreateIcon from '@mui/icons-material/Create';
import { toast } from 'react-toastify';

const OrganizationTab = ({ org, contactInfo = {name: 'NA',email:'NA',phone:'NA', address:'NA'}, onStatusChange, onEditLoading, onEditRefresh }) => {
  const theme = useTheme();

  const [editOpen, setEditOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState(org);
  const [branchData, setBranchData] = useState(null);

  React.useEffect(() => {
    setOrgData(org);
  }, [org]);

  React.useEffect(() => {
    const fetchBranchData = async () => {
      try {
        if (org?.id) {
          const response = await BranchesServices.get({ organization_id: org.id });
          if (response && response.success && response.data?.view?.data && response.data.view.data.length > 0) {
            const activeBranches = response.data.view.data.filter(branch => branch.status === 'Active');
            const mainBranch = activeBranches.find(branch => branch.is_main === true);
            setBranchData(mainBranch || null);
          }
        }
      } catch (error) {
        console.error('Error fetching branch data:', error);
      }
    };

    fetchBranchData();
  }, [org?.id]);

  const handleEditSave = async (e, formData) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('description', formData.description);
      if (formData.logo && formData.logo instanceof File) {
        payload.append('file', formData.logo);
      }
      const response = await OrganizationServices.update(org.id, payload);
      if (response && response.success) {
        toast.success('Organization details updated!');
        setEditOpen(false);

        setOrgData((prev) => ({
          ...prev,
          description: formData.description,
          logo: formData.logo ? URL.createObjectURL(formData.logo) : prev.logo
        }));
      } else {
        toast.error(response?.message || 'Failed to update details.');
        if (onEditRefresh) onEditRefresh();
      }
    } catch (err) {
      toast.error('Error updating details.');
      if (onEditRefresh) onEditRefresh();
    } finally {
      setLoading(false);
    }
  };

  if (!orgData) {
    return <div>No organization data found.</div>;
  }
  return (
    <Box
      className="border-1 border-radius-12"
      sx={{
        background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: 12,
        position: 'relative',
        padding: 0,
        boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)',
        border: '1.5px solid #e0e0e0',
        marginTop:4,
      }}
    >
      {loading ? (
        <Progress />
      ) : (
        <>
          <div className="org-card-header " style={{ 
            background: theme.palette.background.default,
            borderRadius: '10px 10px 0 0', 
            padding: '20px 16px', 
            margin: 0 
          }}>
            <div className="org-logo-section">
              <div className="org-logo">
                <OrgImage
                  orgId={orgData.id}
                  logo={orgData.logo}
                  alt={orgData.short_name || 'Organization Logo'}
                  width={64}
                  height={64}
                  showFullscreen={false}
                  fallbackSrc="/assets/images/no-header-img.jpg"
                  cacheBuster={orgData.logo}

                />
              </div>
              <div className="org-title-section">
                <h2 className="org-title" style={{ color: theme.palette.text.primary }}>{orgData.name}</h2>
                <p className="org-subtitle" style={{ color: theme.palette.text.secondary }}>{orgData.short_name}</p>
                <div className="org-status" style={{ color: theme.palette.text.secondary }}>
                  <span>Color:</span>
                  <div 
                    className="color-dot" 
                    style={{ backgroundColor: orgData.color_code, borderColor: theme.palette.text.secondary }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="org-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
              <Button
                className="edit-btn"
                onClick={() => setEditOpen(true)}
                sx={{
                  background: theme.palette.OrgCardColor?.editbtnx || '#E58653',
                  color: theme.palette.OrgCardColor?.keywordText || 'white',
                  width: 150,
                  fontWeight: 500,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    background: theme.palette.OrgCardColor?.editbtn || '#E58653',
                    opacity: 0.9,
                  },
                }}
                endIcon={<CreateIcon sx={{ color: theme.palette.OrgCardColor?.keywordText || 'white' }} />}
              >
                Edit Profile |
              </Button>
            </div>
            <EditOrganizationModal
              open={editOpen}
              onClose={() => setEditOpen(false)}
              onSave={handleEditSave}
              loading={loading}
              org={orgData}
            />
          </div>

          <div className="org-card-content" style={{ padding: '24px' }}>
            <div className="org-description">
              <h3 style={{ color: theme.palette.text.primary }}>Organization description</h3>
              <p style={{ color: theme.palette.text.secondary }}>{orgData.description || ''}</p>
            </div>

            <div
              className="org-contact"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 20,
              }}
            >
              <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
                <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span style={{ color: theme.palette.text.primary }}>{contactInfo.name}</span>
              </div>
              <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
                <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <span style={{ color: theme.palette.text.primary }}>{contactInfo.email}</span>
              </div>
              <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
                <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 1 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 1 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <span style={{ color: theme.palette.text.primary }}>{contactInfo.contact_no}</span>
              </div>
              <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
                <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <span style={{ color: theme.palette.text.primary }}>
                  {branchData 
                    ? [branchData.address_line1, branchData.address_line2, branchData.address_line3]
                        .filter(line => line && line.trim())
                        .join(' ') 
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <style>{`
            @media (max-width: 600px) {
              .org-contact {
                display: flex !important;
                flex-direction: column !important;
                gap: 16px !important;
              }
            }
          `}</style>
        </>
      )}
    </Box>
  );
};

export default OrganizationTab;
